
import React from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePageAnalysisResults } from '@/hooks/usePageAnalysisResults';
import { AnalysisTabNavigation } from './tabs/AnalysisTabNavigation';
import { AnalysisWarnings } from './tabs/AnalysisWarnings';
import { TabContent } from './tabs/TabContent';
import { TooltipProvider } from '@/components/ui/tooltip';

interface PageDetailsTabProps {
  pageAnalysisResult: DeepAnalysisResult | null;
  urlInput: string;
  errorMessage?: string | null;
}

export const PageDetailsTab: React.FC<PageDetailsTabProps> = ({
  pageAnalysisResult,
  urlInput,
  errorMessage
}) => {
  const isMobile = useIsMobile();
  const { activeTab, setActiveTab, handleExportRecommendations } = usePageAnalysisResults(pageAnalysisResult, urlInput);

  return (
    <TooltipProvider>
      <div className="space-y-6 animate-fade-in">
        <div className="glass-panel rounded-xl p-6 border border-teal-500/20 bg-gradient-to-br from-teal-500/5 to-emerald-500/5 shadow-lg shadow-teal-500/5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
            <h3 className="text-lg font-medium bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
              Page Analysis Results
            </h3>
            <div className="text-sm text-muted-foreground truncate max-w-full sm:max-w-md px-3 py-1 rounded-full border border-teal-500/10 bg-teal-500/5">
              {urlInput}
            </div>
          </div>
          
          {pageAnalysisResult && <AnalysisWarnings pageAnalysisResult={pageAnalysisResult} />}
          
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <AnalysisTabNavigation isMobile={isMobile} />
            
            <TabsContent value="scores" className="animate-fade-in mt-4">
              <TabContent 
                activeTab="scores"
                pageAnalysisResult={pageAnalysisResult!}
                urlInput={urlInput}
                handleExportRecommendations={handleExportRecommendations}
                errorMessage={errorMessage}
              />
            </TabsContent>
            
            <TabsContent value="recommendations" className="animate-fade-in mt-4">
              <TabContent 
                activeTab="recommendations"
                pageAnalysisResult={pageAnalysisResult!}
                urlInput={urlInput}
                handleExportRecommendations={handleExportRecommendations}
                errorMessage={errorMessage}
              />
            </TabsContent>
            
            <TabsContent value="shareability" className="animate-fade-in mt-4">
              <TabContent 
                activeTab="shareability"
                pageAnalysisResult={pageAnalysisResult!}
                urlInput={urlInput}
                handleExportRecommendations={handleExportRecommendations}
                errorMessage={errorMessage}
              />
            </TabsContent>
            
            <TabsContent value="bonusInsights" className="animate-fade-in mt-4">
              <TabContent 
                activeTab="bonusInsights"
                pageAnalysisResult={pageAnalysisResult!}
                urlInput={urlInput}
                handleExportRecommendations={handleExportRecommendations}
                errorMessage={errorMessage}
              />
            </TabsContent>
            
            <TabsContent value="fullReport" className="animate-fade-in mt-4">
              <TabContent 
                activeTab="fullReport"
                pageAnalysisResult={pageAnalysisResult!}
                urlInput={urlInput}
                handleExportRecommendations={handleExportRecommendations}
                errorMessage={errorMessage}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </TooltipProvider>
  );
};
